import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import {
  deleteRoyaltyPayment,
  getRoyaltyPayments,
  updateRoyaltyPayment,
} from '../services/royalty_service'
import './Royalty.css'

const emptyFilters = {
  author: '',
  fromDate: '',
  toDate: '',
  fromAmount: '',
  toAmount: '',
}
const emptyForm = { paymentDate: '', amount: '', comment: '' }

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const getPaymentId = (payment) =>
  getValue(payment, [
    'paymentid',
    'payment_id',
    'royalty_paymentid',
    'royalty_payment_id',
    'id',
  ])
const getPaymentDate = (payment) =>
  getValue(payment, ['date_paid', 'pay_date', 'date'])
const getPaymentAmount = (payment) =>
  getValue(payment, ['amt_paid', 'amount', 'paid_amount'], 0)
const getPaymentAuthor = (payment) =>
  getValue(payment.authors, ['author_name', 'name'], '') ||
  getValue(payment, ['author_name', 'author'])
const getPaymentPaidBy = (payment) =>
  getValue(payment.users, ['user_fullname', 'fullname', 'name'], '') ||
  getValue(payment, ['pay_by_name', 'paid_by_name', 'pay_by', 'paid_by'])
const formatDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
}
const toDateInputValue = (value) => {
  if (!value || value === '-') return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10)
}
const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)

function RoyaltyPayment() {
  const { user, hasPermission } = useAuth()
  const canEditRoyaltyPayment = hasPermission('editRoyaltyPayment')
  const canDeleteRoyaltyPayment = hasPermission('deleteRoyaltyPayment')
  const userId = getValue(user, ['userid', 'user_id', 'id'], '')
  const [filters, setFilters] = useState(emptyFilters)
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters)
  const [isQueryOpen, setIsQueryOpen] = useState(false)
  const [payments, setPayments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [editingPayment, setEditingPayment] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [formError, setFormError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [paymentToDelete, setPaymentToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const loadPayments = async (query = appliedFilters) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getRoyaltyPayments({ page: 1, ...query })
      setPayments(result.payments)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadPayments(emptyFilters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateFilters = (event) => {
    const { name, value } = event.target
    setFilters((current) => ({ ...current, [name]: value }))
  }
  const searchPayments = (event) => {
    event.preventDefault()
    setAppliedFilters(filters)
    loadPayments(filters)
  }
  const clearFilters = () => {
    setFilters(emptyFilters)
    setAppliedFilters(emptyFilters)
    loadPayments(emptyFilters)
  }
  const openEditModal = (payment) => {
    setEditingPayment(payment)
    setForm({
      paymentDate: toDateInputValue(getPaymentDate(payment)),
      amount: Number(getPaymentAmount(payment)).toFixed(2),
      comment: getValue(payment, ['comment', 'payment_comment', 'remarks'], ''),
    })
    setFormError('')
  }
  const closeEditModal = () => {
    if (!isSaving) setEditingPayment(null)
  }
  const updateForm = (event) => {
    const { name, value } = event.target
    setForm((current) => ({ ...current, [name]: value }))
  }
  const savePayment = async (event) => {
    event.preventDefault()
    if (!editingPayment) return
    const amount = Number(form.amount)
    if (!Number.isFinite(amount) || amount <= 0) {
      setFormError('Enter a payment amount greater than zero.')
      return
    }

    setIsSaving(true)
    setFormError('')
    try {
      await updateRoyaltyPayment(getPaymentId(editingPayment), {
        payment_date: form.paymentDate,
        amount,
        comment: form.comment.trim(),
        pay_by: userId,
      })
      setEditingPayment(null)
      await loadPayments()
    } catch (requestError) {
      setFormError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }
  const confirmDelete = async () => {
    if (!paymentToDelete) return
    setIsDeleting(true)
    setError('')
    try {
      await deleteRoyaltyPayment(getPaymentId(paymentToDelete))
      setPaymentToDelete(null)
      await loadPayments()
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className='royalty-page'>
      <nav className='royalty-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <Link to='/admin/royalties'>Royalties</Link>
        <span aria-hidden='true'>/</span>
        <strong>Royalty payments</strong>
      </nav>
      <header className='royalty-page__header'>
        <div>
          <span className='app-content__eyebrow'>Finance</span>
          <h1>Royalty payments</h1>
          <p>Review and maintain payments made to authors.</p>
        </div>
      </header>
      {error && (
        <div className='royalty-page__error' role='alert'>
          {error}
        </div>
      )}

      <section className='royalty-query-card'>
        <button
          className='royalty-query-card__toggle'
          type='button'
          onClick={() => setIsQueryOpen((open) => !open)}
          aria-expanded={isQueryOpen}
        >
          <span>
            <span className='app-content__eyebrow'>Refine results</span>
            <strong>Payment query</strong>
          </span>
          <span aria-hidden='true'>{isQueryOpen ? '-' : '+'}</span>
        </button>
        {isQueryOpen && (
          <form className='royalty-query-form' onSubmit={searchPayments}>
            <label>
              Author
              <input
                name='author'
                value={filters.author}
                onChange={updateFilters}
              />
            </label>
            <label>
              From date
              <input
                name='fromDate'
                type='date'
                value={filters.fromDate}
                onChange={updateFilters}
              />
            </label>
            <label>
              To date
              <input
                name='toDate'
                type='date'
                value={filters.toDate}
                onChange={updateFilters}
              />
            </label>
            <label>
              From amount
              <input
                name='fromAmount'
                type='number'
                min='0'
                step='0.01'
                value={filters.fromAmount}
                onChange={updateFilters}
              />
            </label>
            <label>
              To amount
              <input
                name='toAmount'
                type='number'
                min='0'
                step='0.01'
                value={filters.toAmount}
                onChange={updateFilters}
              />
            </label>
            <div className='royalty-query-form__actions'>
              <button
                className='royalty-button royalty-button--primary'
                type='submit'
              >
                Search
              </button>
              <button
                className='royalty-button'
                type='button'
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
          </form>
        )}
      </section>

      <section
        className='royalty-table-card'
        aria-labelledby='royalty-payment-list-title'
      >
        <div className='royalty-table-card__header'>
          <span className='app-content__eyebrow'>Payment register</span>
          <h2 id='royalty-payment-list-title'>Royalty payment list</h2>
          <p>{payments.length} payments</p>
        </div>
        <div className='royalty-table-wrap'>
          <table className='royalty-table'>
            <thead>
              <tr>
                <th>Payment ID</th>
                <th>Date</th>
                <th>Author</th>
                <th>Royalty ID</th>
                <th>Paid by</th>
                <th>Amount</th>
                <th>Comment</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='royalty-table__empty' colSpan='8'>
                    Loading royalty payments...
                  </td>
                </tr>
              ) : payments.length ? (
                payments.map((payment, index) => (
                  <tr key={`${getPaymentId(payment)}-${index}`}>
                    <td>
                      <strong>{getPaymentId(payment)}</strong>
                    </td>
                    <td>{formatDate(getPaymentDate(payment))}</td>
                    <td>{getPaymentAuthor(payment)}</td>
                    <td>
                      {getValue(payment, ['royalty_computeid', 'royalty_id'])}
                    </td>
                    <td>{getPaymentPaidBy(payment)}</td>
                    <td className='royalty-table__amount'>
                      {formatCurrency(getPaymentAmount(payment))}
                    </td>
                    <td>
                      {getValue(payment, [
                        'comment',
                        'payment_comment',
                        'remarks',
                      ])}
                    </td>
                    <td>
                      <div className='royalty-actions'>
                        {canEditRoyaltyPayment && (
                          <button
                            type='button'
                            onClick={() => openEditModal(payment)}
                          >
                            Edit
                          </button>
                        )}
                        {canDeleteRoyaltyPayment && (
                          <button
                            className='royalty-actions__delete'
                            type='button'
                            onClick={() => setPaymentToDelete(payment)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className='royalty-table__empty' colSpan='8'>
                    No royalty payments match the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {editingPayment && (
        <div
          className='royalty-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget && closeEditModal()
          }
        >
          <section
            className='royalty-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='edit-royalty-payment-title'
          >
            <div className='royalty-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Edit payment</span>
                <h2 id='edit-royalty-payment-title'>
                  Royalty payment {getPaymentId(editingPayment)}
                </h2>
              </div>
              <button
                type='button'
                onClick={closeEditModal}
                disabled={isSaving}
                aria-label='Close edit royalty payment form'
              >
                x
              </button>
            </div>
            {formError && (
              <div className='royalty-page__error' role='alert'>
                {formError}
              </div>
            )}
            <form className='royalty-form' onSubmit={savePayment}>
              <label>
                Payment date
                <input
                  name='paymentDate'
                  type='date'
                  value={form.paymentDate}
                  onChange={updateForm}
                  required
                />
              </label>
              <label>
                Amount
                <input
                  name='amount'
                  type='number'
                  min='0.01'
                  step='0.01'
                  value={form.amount}
                  onChange={updateForm}
                  required
                />
              </label>
              <label className='royalty-form__full'>
                Comment
                <textarea
                  name='comment'
                  rows='3'
                  value={form.comment}
                  onChange={updateForm}
                />
              </label>
              <div className='royalty-form__actions'>
                <button
                  className='royalty-button'
                  type='button'
                  onClick={closeEditModal}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  className='royalty-button royalty-button--primary'
                  type='submit'
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save changes'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {paymentToDelete && (
        <div
          className='royalty-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget &&
            !isDeleting &&
            setPaymentToDelete(null)
          }
        >
          <section
            className='royalty-modal'
            role='dialog'
            aria-modal='true'
            aria-labelledby='delete-royalty-payment-title'
          >
            <div className='royalty-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Confirm deletion</span>
                <h2 id='delete-royalty-payment-title'>
                  Delete royalty payment
                </h2>
              </div>
              <button
                type='button'
                onClick={() => setPaymentToDelete(null)}
                disabled={isDeleting}
                aria-label='Close deletion confirmation'
              >
                x
              </button>
            </div>
            <p className='royalty-payment-summary'>
              Delete royalty payment {getPaymentId(paymentToDelete)}? This
              cannot be undone.
            </p>
            <div className='royalty-form__actions'>
              <button
                className='royalty-button'
                type='button'
                onClick={() => setPaymentToDelete(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className='royalty-button royalty-actions__delete'
                type='button'
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete payment'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default RoyaltyPayment
